import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from "react-bootstrap";
import API from "../services/api";
import "./AdminRequirementDispatches.css";

const reverseGeocode = async (lat, lng) => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
        lat
      )}&lon=${encodeURIComponent(lng)}`
    );
    if (!res.ok) return "";
    const data = await res.json();
    return data?.display_name || "";
  } catch {
    return "";
  }
};

const fmt = (v) =>
  v
    ? new Date(v).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "—";

const statusTone = (status = "") => {
  const s = String(status).toLowerCase();
  if (s === "delivered" || s === "received") return "success";
  if (s === "returned") return "dark";
  if (s === "cancelled") return "danger";
  if (s === "delayed") return "warning";
  if (s === "dispatched" || s === "in-transit" || s === "out-for-delivery") return "info";
  return "secondary";
};

const initialForm = {
  status: "dispatched",
  trackingId: "",
  transporterName: "",
  vehicleNumber: "",
  driverName: "",
  driverMobile: "",
  dispatchDate: "",
  expectedDeliveryDate: "",
  currentLocation: "",
  notes: "",
  timelineNote: "",
};

const requirementAmountOf = (r = {}) => Number(r?.requirementAmount ?? r?.payment?.latestAmountRupees ?? 0);
const cleanText = (value, fallback = "") => {
  const text = String(value || "").trim();
  return text || fallback;
};

const buildVendorDispatchParams = (row, dispatchForm) => {
  const vendorName = cleanText(row?.vendor?.companyName || row?.vendor?.fullName, "Vendor");
  return [
    vendorName,
    cleanText(dispatchForm?.trackingId),
    cleanText(dispatchForm?.transporterName, "N/A"),
    cleanText(dispatchForm?.driverName, "N/A"),
    cleanText(dispatchForm?.driverMobile, "N/A"),
  ];
};

export default function AdminRequirementDispatches() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [waStatus, setWaStatus] = useState({ type: "", text: "" });
  const [saving, setSaving] = useState(false);
  const [policySaving, setPolicySaving] = useState(false);
  const [returnPercent, setReturnPercent] = useState("0");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(initialForm);

  const load = async () => {
    try {
      const { data } = await API.get("/admin/vendor-requirement-dispatches");
      setRows(Array.isArray(data) ? data : []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load requirement dispatches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadReturnPolicy();
  }, []);

  const loadReturnPolicy = async () => {
    try {
      const { data } = await API.get("/admin/return-policy");
      setReturnPercent(String(Number(data?.deductionPercent || 0)));
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load return deduction policy");
    }
  };

  const saveReturnPolicy = async () => {
    const val = Number(returnPercent);
    if (!Number.isFinite(val) || val < 0 || val > 100) {
      setError("Return deduction percentage must be between 0 and 100");
      return;
    }
    try {
      setPolicySaving(true);
      await API.put("/admin/return-policy", { deductionPercent: val });
      setError("");
      await loadReturnPolicy();
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to update return deduction policy");
    } finally {
      setPolicySaving(false);
    }
  };

  const openDispatch = (row) => {
    const dispatch = row?.dispatch || {};
    setSelected(row);
    setForm({
      status: dispatch.status || "dispatched",
      trackingId: dispatch.trackingId || "",
      transporterName: dispatch.transporterName || "",
      vehicleNumber: dispatch.vehicleNumber || "",
      driverName: dispatch.driverName || "",
      driverMobile: dispatch.driverMobile || "",
      dispatchDate: dispatch.dispatchDate ? new Date(dispatch.dispatchDate).toISOString().slice(0, 16) : "",
      expectedDeliveryDate: dispatch.expectedDeliveryDate
        ? new Date(dispatch.expectedDeliveryDate).toISOString().slice(0, 16)
        : "",
      currentLocation: dispatch.currentLocation || "",
      notes: dispatch.notes || "",
      timelineNote: "",
    });
  };

  const closeDispatch = () => {
    setSelected(null);
    setForm(initialForm);
  };

  const saveDispatch = async () => {
    if (!selected?._id) return;
    const dispatchForm = {
      ...form,
      trackingId: cleanText(form.trackingId),
      transporterName: cleanText(form.transporterName),
      driverName: cleanText(form.driverName),
      driverMobile: cleanText(form.driverMobile),
    };

    if (!dispatchForm.trackingId) {
      setError("Tracking ID required");
      return;
    }
    try {
      setSaving(true);
      setWaStatus({ type: "", text: "" });
      const res = await API.patch(`/admin/vendor-requirements/${selected._id}/dispatch`, {
        ...dispatchForm,
      });
      const wa = res?.data?.whatsapp;
      if (wa?.ok) {
        setWaStatus({
          type: "success",
          text: "Dispatch updated and WhatsApp sent successfully",
        });
        setError("");
      } else if (wa?.error) {
        setWaStatus({
          type: "danger",
          text: `Dispatch saved but WhatsApp failed: ${wa.error}`,
        });
      } else if (wa?.skipped) {
        setWaStatus({
          type: "warning",
          text: `Dispatch saved but WhatsApp skipped: ${wa.reason || "unknown reason"}`,
        });
      } else {
        setWaStatus({
          type: "warning",
          text: "Dispatch updated. WhatsApp status unavailable.",
        });
      }
      closeDispatch();
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to update dispatch");
    } finally {
      setSaving(false);
    }
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported in this browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = Number(pos.coords.latitude || 0).toFixed(6);
        const lng = Number(pos.coords.longitude || 0).toFixed(6);
        const readable = await reverseGeocode(lat, lng);
        setForm((p) => ({ ...p, currentLocation: readable || "Location captured" }));
        setError("");
      },
      (err) => {
        setError(err?.message || "Unable to fetch current location");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const totals = useMemo(() => {
    const out = { total: rows.length, active: 0, delivered: 0, delayed: 0 };
    rows.forEach((r) => {
      const s = String(r?.dispatch?.status || "pending").toLowerCase();
      if (["dispatched", "in-transit", "out-for-delivery"].includes(s)) out.active += 1;
      if (s === "delivered") out.delivered += 1;
      if (s === "delayed") out.delayed += 1;
    });
    return out;
  }, [rows]);

  return (
    <div className="container-fluid py-3">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h5 className="mb-1">Requirement Dispatch Center</h5>
              <div className="text-muted small">Admin dispatch + vendor tracking notifications</div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Badge bg="secondary">Total {totals.total}</Badge>
              <Badge bg="info">Active {totals.active}</Badge>
              <Badge bg="success">Delivered {totals.delivered}</Badge>
              <Badge bg="warning" text="dark">
                Delayed {totals.delayed}
              </Badge>
              <Button size="sm" variant="outline-primary" onClick={load}>
                Refresh
              </Button>
            </div>
          </div>

          <div className="ard-policy-card mt-3">
            <div className="ard-policy-title">Return Deduction Policy</div>
            <div className="ard-policy-row">
              <Form.Control
                className="ard-input"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={returnPercent}
                onChange={(e) => setReturnPercent(e.target.value)}
                placeholder="0-100"
              />
              <span className="ard-policy-unit">%</span>
              <Button size="sm" className="ard-save-btn" onClick={saveReturnPolicy} disabled={policySaving}>
                {policySaving ? "Saving..." : "Save Policy"}
              </Button>
            </div>
            <div className="ard-hint">Vendor return confirmation will auto-calculate deduction by this percentage.</div>
          </div>

          {error ? <Alert className="mt-3 mb-0" variant="danger">{error}</Alert> : null}
          {waStatus.text ? <Alert className="mt-3 mb-0" variant={waStatus.type || "info"}>{waStatus.text}</Alert> : null}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : rows.length === 0 ? (
            <Alert className="mt-3 mb-0" variant="info">
              No approved requirements available for dispatch.
            </Alert>
          ) : (
            <div className="table-responsive mt-3">
              <Table hover bordered className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Requirement</th>
                    <th>Vendor</th>
                    <th>Product</th>
                    <th>Amount</th>
                    <th>Tracking</th>
                    <th>Status</th>
                    <th>Dispatch</th>
                    <th>ETA</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const reqCode = `REQ-${String(r?._id || "").slice(-6).toUpperCase()}`;
                    const d = r.dispatch || {};
                    return (
                      <tr key={r._id}>
                        <td>
                          <div className="fw-semibold">{reqCode}</div>
                          <div className="small text-muted">{r.locationText || "—"}</div>
                        </td>
                        <td>
                          <div className="fw-semibold">{r.vendor?.companyName || r.vendor?.fullName || "—"}</div>
                          <div className="small text-muted">{r.vendor?.mobile || "—"}</div>
                        </td>
                        <td>
                          {r.productType || "—"} ({r.quantity ?? "—"} {r.unit || ""})
                        </td>
                        <td>
                          {requirementAmountOf(r) > 0
                            ? `₹ ${requirementAmountOf(r).toLocaleString("en-IN")}`
                            : "—"}
                        </td>
                        <td>{d.trackingId || "—"}</td>
                        <td>
                          <Badge bg={statusTone(d.status)}>{d.status || "pending"}</Badge>
                        </td>
                        <td>{fmt(d.dispatchDate)}</td>
                        <td>{fmt(d.expectedDeliveryDate)}</td>
                        <td>
                          <Button size="sm" onClick={() => openDispatch(r)}>
                            Update Dispatch
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={!!selected}
        onHide={closeDispatch}
        size="lg"
        centered
        dialogClassName="ard-modal"
        contentClassName="ard-modal-content"
        backdropClassName="ard-backdrop"
      >
        <Modal.Header closeButton className="ard-modal-head">
          <Modal.Title className="ard-modal-title">Update Dispatch Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="ard-modal-body">
          <Form className="ard-form">
            <div className="row g-3 g-md-4">
              <div className="col-md-4">
                <Form.Label className="ard-label">Status</Form.Label>
                <Form.Select
                  className="ard-input"
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                >
                  <option value="pending">pending</option>
                  <option value="dispatched">dispatched</option>
                  <option value="in-transit">in-transit</option>
                  <option value="out-for-delivery">out-for-delivery</option>
                  <option value="delivered">delivered</option>
                  <option value="delayed">delayed</option>
                  <option value="cancelled">cancelled</option>
                </Form.Select>
              </div>
              <div className="col-md-8">
                <Form.Label className="ard-label d-flex align-items-center justify-content-between">
                  Tracking ID <span className="ard-required">Required</span>
                </Form.Label>
                <Form.Control
                  className="ard-input"
                  value={form.trackingId}
                  onChange={(e) => setForm((p) => ({ ...p, trackingId: e.target.value }))}
                  placeholder="e.g. TRK-8293-2910"
                />
              </div>
              <div className="col-md-6">
                <Form.Label className="ard-label">Transporter Name</Form.Label>
                <Form.Control
                  className="ard-input"
                  value={form.transporterName}
                  onChange={(e) => setForm((p) => ({ ...p, transporterName: e.target.value }))}
                />
              </div>
              <div className="col-md-6">
                <Form.Label className="ard-label">Vehicle Number</Form.Label>
                <Form.Control
                  className="ard-input"
                  value={form.vehicleNumber}
                  onChange={(e) => setForm((p) => ({ ...p, vehicleNumber: e.target.value }))}
                />
              </div>
              <div className="col-md-6">
                <Form.Label className="ard-label">Driver Name</Form.Label>
                <Form.Control
                  className="ard-input"
                  value={form.driverName}
                  onChange={(e) => setForm((p) => ({ ...p, driverName: e.target.value }))}
                />
              </div>
              <div className="col-md-6">
                <Form.Label className="ard-label">Driver Mobile</Form.Label>
                <Form.Control
                  className="ard-input"
                  value={form.driverMobile}
                  onChange={(e) => setForm((p) => ({ ...p, driverMobile: e.target.value }))}
                  placeholder="e.g. 98XXXXXXXX"
                />
              </div>
              <div className="col-md-6">
                <Form.Label className="ard-label">Dispatch Date</Form.Label>
                <Form.Control
                  className="ard-input"
                  type="datetime-local"
                  value={form.dispatchDate}
                  onChange={(e) => setForm((p) => ({ ...p, dispatchDate: e.target.value }))}
                />
              </div>
              <div className="col-md-6">
                <Form.Label className="ard-label">Expected Delivery Date</Form.Label>
                <Form.Control
                  className="ard-input"
                  type="datetime-local"
                  value={form.expectedDeliveryDate}
                  onChange={(e) => setForm((p) => ({ ...p, expectedDeliveryDate: e.target.value }))}
                />
              </div>
              <div className="col-md-12">
                <Form.Label className="ard-label d-flex align-items-center justify-content-between">
                  Current Location
                  <button type="button" className="ard-link-btn" onClick={useCurrentLocation}>
                    Use Current
                  </button>
                </Form.Label>
                <Form.Control
                  className="ard-input"
                  value={form.currentLocation}
                  onChange={(e) => setForm((p) => ({ ...p, currentLocation: e.target.value }))}
                  placeholder="Current city / hub / checkpoint"
                />
              </div>
              <div className="col-md-12">
                <Form.Label className="ard-label">Dispatch Notes</Form.Label>
                <Form.Control
                  className="ard-input"
                  as="textarea"
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  placeholder="Internal logistics notes..."
                />
              </div>
              <div className="col-md-12">
                <Form.Label className="ard-label">Timeline Update Note</Form.Label>
                <Form.Control
                  className="ard-input"
                  as="textarea"
                  rows={2}
                  value={form.timelineNote}
                  onChange={(e) => setForm((p) => ({ ...p, timelineNote: e.target.value }))}
                  placeholder="This will be added to tracking timeline"
                />
                <div className="ard-hint">Visibility: Public (shown to vendor)</div>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer className="ard-modal-foot">
          <Button variant="light" className="ard-cancel-btn" onClick={closeDispatch}>
            Cancel
          </Button>
          <Button className="ard-save-btn" onClick={saveDispatch} disabled={saving}>
            {saving ? "Saving..." : "Save Dispatch"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
